/**
 * 1:1 session: joins X3DH (startup) + double ratchet (per message) and models the
 * application CONTENT (text/media, timestamp, expiration).
 *
 * The content is encrypted inside the ratchet; the server/relay only sees the
 * {@link WireEnvelope} (routing + opaque blob). The "expiring messages" are
 * a client policy: the `expiresInSecs` travels ENCRYPTED and each end applies
 * it locally ({@link isExpired}) — the relay cannot know that a
 * message was ephemeral.
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "./crypto/encoding";
import type { RawKeyPair } from "./crypto/dh";
import {
  initRatchetInitiator,
  initRatchetResponder,
  ratchetDecrypt,
  ratchetEncrypt,
  type RatchetMessage,
  type RatchetState,
} from "./double-ratchet";
import type { DeviceIdentity, PreKeyBundle } from "./identity";
import { initiateSession, respondSession, type X3dhInitHeader } from "./x3dh";

export interface MessageContent {
  readonly kind: "text" | "media" | "control";
  readonly body: string;
  /** Opaque reference to E2E media (e.g. encrypted blob in BYOS); not the binary. */
  readonly mediaRef?: string;
  readonly sentAt: number;
  /** If present, the message must be hidden after `sentAt + expiresInSecs`. */
  readonly expiresInSecs?: number;
}

export interface Session {
  readonly peerDeviceId: string;
  ratchet: RatchetState;
  associatedData: Uint8Array;
  /** X3DH header to attach until the peer confirms (initiator only). */
  pendingInit?: X3dhInitHeader;
}

/** Envelope that travels over the transport (P2P or relay). The relay only reads routing. */
export interface WireEnvelope {
  readonly v: 1;
  readonly toDevice: string;
  readonly fromDevice: string;
  /** Space/organization (multi-org isolation); empty = 1:1 guest. */
  readonly spaceId?: string;
  readonly type: "prekey" | "normal";
  /** Present only in the first message (asynchronous X3DH startup). */
  readonly x3dh?: {
    readonly identityDhPub: string;
    readonly ephemeralPub: string;
    readonly usedSignedPreKeyId: string;
    readonly usedOneTimePreKeyId?: string;
  };
  /** Ratchet header (protocol metadata, NOT content). */
  readonly rh: { readonly dhPub: string; readonly pn: number; readonly n: number };
  /** OPAQUE blob: encrypted content. The relay cannot open it. */
  readonly ct: string;
}

// --- Session startup --------------------------------------------------------

/** Alice initiates toward Bob (who may be offline) using his public bundle. */
export async function startInitiatorSession(
  self: DeviceIdentity,
  peerDeviceId: string,
  bundle: PreKeyBundle,
): Promise<Session> {
  const init = await initiateSession(self, bundle);
  const ratchet = await initRatchetInitiator(init.sharedSecret, init.peerSignedPreKeyPub);
  return {
    peerDeviceId,
    ratchet,
    associatedData: init.associatedData,
    pendingInit: init.header,
  };
}

/** Bob receives Alice's first message and establishes the session. */
export async function acceptInitiatorSession(
  self: DeviceIdentity,
  selfSignedPreKey: RawKeyPair,
  peerDeviceId: string,
  header: X3dhInitHeader,
): Promise<Session> {
  const res = await respondSession(self, header);
  const ratchet = initRatchetResponder(res.sharedSecret, selfSignedPreKey);
  return { peerDeviceId, ratchet, associatedData: res.associatedData };
}

// --- Send / receive ---------------------------------------------------------

function encodeContent(content: MessageContent): Uint8Array {
  return utf8Encode(JSON.stringify(content));
}

export async function encryptMessage(
  self: DeviceIdentity,
  session: Session,
  content: MessageContent,
  spaceId?: string,
): Promise<WireEnvelope> {
  const msg = await ratchetEncrypt(session.ratchet, encodeContent(content), session.associatedData);
  const env: WireEnvelope = {
    v: 1,
    toDevice: session.peerDeviceId,
    fromDevice: self.deviceId,
    spaceId,
    type: session.pendingInit ? "prekey" : "normal",
    x3dh: session.pendingInit
      ? {
          identityDhPub: bytesToBase64(session.pendingInit.identityDhPub),
          ephemeralPub: bytesToBase64(session.pendingInit.ephemeralPub),
          usedSignedPreKeyId: session.pendingInit.usedSignedPreKeyId,
          usedOneTimePreKeyId: session.pendingInit.usedOneTimePreKeyId,
        }
      : undefined,
    rh: { dhPub: bytesToBase64(msg.header.dhPub), pn: msg.header.pn, n: msg.header.n },
    ct: bytesToBase64(msg.ciphertext),
  };
  return env;
}

function wireToRatchetMessage(env: WireEnvelope): RatchetMessage {
  return {
    header: { dhPub: base64ToBytes(env.rh.dhPub), pn: env.rh.pn, n: env.rh.n },
    ciphertext: base64ToBytes(env.ct),
  };
}

export function parseX3dhHeader(env: WireEnvelope): X3dhInitHeader {
  if (!env.x3dh) {
    throw new Error("envelope no trae cabecera X3DH");
  }
  return {
    identityDhPub: base64ToBytes(env.x3dh.identityDhPub),
    ephemeralPub: base64ToBytes(env.x3dh.ephemeralPub),
    usedSignedPreKeyId: env.x3dh.usedSignedPreKeyId,
    usedOneTimePreKeyId: env.x3dh.usedOneTimePreKeyId,
  };
}

function toMessageContent(value: unknown): MessageContent {
  if (typeof value !== "object" || value === null) {
    throw new Error("invalid decrypted content");
  }
  const record: Record<string, unknown> = { ...value };
  const { kind } = record;
  if (kind !== "text" && kind !== "media" && kind !== "control") {
    throw new Error("decrypted content: invalid kind");
  }
  if (typeof record.body !== "string" || typeof record.sentAt !== "number") {
    throw new TypeError("decrypted content: required fields missing");
  }
  return {
    kind,
    body: record.body,
    sentAt: record.sentAt,
    mediaRef: typeof record.mediaRef === "string" ? record.mediaRef : undefined,
    expiresInSecs: typeof record.expiresInSecs === "number" ? record.expiresInSecs : undefined,
  };
}

export async function decryptMessage(session: Session, env: WireEnvelope): Promise<MessageContent> {
  const plaintext = await ratchetDecrypt(
    session.ratchet,
    wireToRatchetMessage(env),
    session.associatedData,
  );
  return toMessageContent(JSON.parse(utf8Decode(plaintext)));
}

/** The initiator stops attaching the X3DH header after receiving the peer's reply. */
export function markInitConfirmed(session: Session): void {
  session.pendingInit = undefined;
}

// --- Expiring messages ------------------------------------------------------

export function isExpired(content: MessageContent, now = Date.now()): boolean {
  if (content.expiresInSecs === undefined) {
    return false;
  }
  return now >= content.sentAt + content.expiresInSecs * 1000;
}
